import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation, useI18nStore } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import { useDomain, canRegister } from '../lib/domain';
import { useSystemSettings } from '../hooks/useSystemSettings';
import { useTheme } from '../hooks/useTheme';
import { authenticateBiometric, hasBiometricCredentials, isWebAuthnSupported } from '../lib/webauthn';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Eye, EyeOff, Loader2, Globe, Moon, Sun, Fingerprint } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LANGUAGES = [
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }
];

/* ── color palette per theme ── */
const palette = {
  dark: {
    particle: [56, 189, 248],   // cyan
    hex: 'rgba(56,189,248,0.12)',
    scan: 'rgba(56,189,248,0.4)',
    data: 'rgba(56,189,248,0.18)',
  },
  light: {
    particle: [14, 116, 144],   // teal-700
    hex: 'rgba(14,116,144,0.10)',
    scan: 'rgba(14,116,144,0.25)',
    data: 'rgba(14,116,144,0.12)',
  },
};

/* ───── animated background canvas ───── */
function NetworkCanvas({ isDark }) {
  const ref = useRef(null);
  const colRef = useRef(isDark ? palette.dark.particle : palette.light.particle);

  useEffect(() => {
    colRef.current = isDark ? palette.dark.particle : palette.light.particle;
  }, [isDark]);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let particles = [];
    const COUNT = 70;
    const DIST = 140;

    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width, y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.6, vy: (Math.random() - 0.5) * 0.6,
        r: Math.random() * 2 + 1, pulse: Math.random() * Math.PI * 2,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const [cr, cg, cb] = colRef.current;

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(${cr},${cg},${cb},${(1 - d / DIST) * 0.25})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.pulse += 0.03;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        const glow = 0.5 + Math.sin(p.pulse) * 0.3;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${cr},${cg},${cb},${glow})`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${cr},${cg},${cb},${glow * 0.1})`;
        ctx.fill();
      });

      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 w-full h-full" />;
}

/* ───── hex grid ───── */
function HexGrid({ isDark }) {
  const stroke = isDark ? palette.dark.hex : palette.light.hex;
  const hexes = useRef(Array.from({ length: 12 }, (_, i) => ({
    id: i, left: `${10 + Math.random() * 80}%`, top: `${5 + Math.random() * 90}%`,
    delay: `${Math.random() * 6}s`, size: 20 + Math.random() * 40, dur: `${8 + Math.random() * 8}s`,
  }))).current;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {hexes.map(h => (
        <div key={h.id} className="absolute animate-float-hex" style={{ left: h.left, top: h.top, animationDelay: h.delay, animationDuration: h.dur }}>
          <svg width={h.size} height={h.size} viewBox="0 0 100 100" fill="none">
            <polygon points="50,3 93,25 93,75 50,97 7,75 7,25" stroke={stroke} strokeWidth="1.5" fill="none" />
          </svg>
        </div>
      ))}
    </div>
  );
}

/* ───── scan line ───── */
function ScanLine({ isDark }) {
  const color = isDark ? palette.dark.scan : palette.light.scan;
  return <div className="absolute inset-x-0 h-px animate-scan-line" style={{ backgroundImage: `linear-gradient(to right, transparent, ${color}, transparent)` }} />;
}

/* ───── data stream ───── */
function DataStream({ side, isDark }) {
  const color = isDark ? palette.dark.data : palette.light.data;
  const cols = useRef(Array.from({ length: 4 }, (_, i) => ({
    id: i,
    text: Array.from({ length: 24 }, () => '01'[Math.floor(Math.random() * 2)]).join('\n'),
    delay: `${i * 1.2}s`,
    x: side === 'left' ? `${4 + i * 3}%` : `${84 + i * 3}%`,
  }))).current;

  return (
    <>
      {cols.map(c => (
        <pre key={c.id} className="absolute text-[9px] leading-tight font-mono whitespace-pre animate-data-fall pointer-events-none select-none"
          style={{ left: c.x, top: '-10%', animationDelay: c.delay, animationDuration: '12s', color }}>
          {c.text}
        </pre>
      ))}
    </>
  );
}

/* ══════════ MAIN ══════════ */
export const Login2 = () => {
  const { t } = useTranslation();
  const { language, setLanguage } = useI18nStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, login2fa, isLoading } = useAuthStore();
  const { settings } = useSystemSettings();
  const { theme, toggleTheme } = useTheme();
  const domainConfig = useDomain();

  const isDark = theme === 'dark';
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [mounted, setMounted] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [rememberedEmail, setRememberedEmail] = useState('');

  // 2FA state
  const [twoFAStep, setTwoFAStep] = useState(false);
  const [twoFAUserId, setTwoFAUserId] = useState('');
  const [twoFAUserName, setTwoFAUserName] = useState('');
  const [twoFARememberMe, setTwoFARememberMe] = useState(false);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [twoFAError, setTwoFAError] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  useEffect(() => { setMounted(true); }, []);

  // Check for remembered email and biometric availability
  useEffect(() => {
    const stored = localStorage.getItem('rememberedEmail');
    if (stored) {
      setRememberedEmail(stored);
      setFormData(prev => ({ ...prev, email: stored }));
      setRememberMe(true);
      // Check biometric
      if (isWebAuthnSupported()) {
        hasBiometricCredentials(stored).then(has => {
          setBiometricAvailable(has);
        });
      }
    }
  }, []);

  // Impersonation
  useEffect(() => {
    const tok = searchParams.get('impersonate');
    if (!tok) return;
    localStorage.setItem('hr-auth-storage', JSON.stringify({ state: { user: null, accessToken: tok, refreshToken: null, isAuthenticated: true, isLoading: false, error: null }, version: 0 }));
    window.location.replace('/dashboard');
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(formData.email, formData.password, rememberMe);
    if (result.success) {
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', formData.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      toast.success(t('auth.loginSuccess')); 
      navigate('/dashboard');
    }
    else if (result.requires_2fa) {
      setTwoFAStep(true);
      setTwoFAUserId(result.user_id);
      setTwoFAUserName(result.user_name);
      setTwoFARememberMe(result.remember_me || rememberMe);
      setTimeout(() => otpRefs[0]?.current?.focus(), 400);
    }
    else { toast.error(result.error); }
  };

  const handleBiometricLogin = useCallback(async () => {
    if (!rememberedEmail || biometricLoading) return;
    setBiometricLoading(true);
    try {
      const result = await authenticateBiometric(rememberedEmail);
      if (result.success && result.data) {
        const { setTokens, fetchUser } = useAuthStore.getState();
        setTokens(result.data.access_token, result.data.refresh_token);
        await fetchUser();
        toast.success(t('auth.loginSuccess'));
        navigate('/dashboard');
      }
    } catch (err) {
      const msg = err.message || 'Biometric authentication failed';
      if (msg.includes('NO_CREDENTIALS')) {
        setBiometricAvailable(false);
        toast.error(t('auth.biometricNotRegistered') || 'Bu cihazda biyometrik kayıt bulunamadı');
      } else if (!msg.includes('NotAllowedError') && !msg.includes('abort')) {
        toast.error(msg);
      }
    } finally {
      setBiometricLoading(false);
    }
  }, [rememberedEmail, biometricLoading, navigate, t]);

  const handleOtpChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otpDigits];
    next[idx] = val;
    setOtpDigits(next);
    setTwoFAError('');
    if (val && idx < 5) otpRefs[idx + 1]?.current?.focus();
    // Auto-submit when all 6 digits filled
    if (val && idx === 5 && next.every(d => d)) {
      submit2FA(next.join(''));
    }
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otpDigits[idx] && idx > 0) {
      otpRefs[idx - 1]?.current?.focus();
    }
    if (e.key === 'Enter' && otpDigits.every(d => d)) {
      submit2FA(otpDigits.join(''));
    }
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      const digits = paste.split('');
      setOtpDigits(digits);
      otpRefs[5]?.current?.focus();
      setTimeout(() => submit2FA(paste), 100);
      e.preventDefault();
    }
  };

  const submit2FA = async (code) => {
    setTwoFALoading(true);
    setTwoFAError('');
    const result = await login2fa(twoFAUserId, code, false, twoFARememberMe);
    setTwoFALoading(false);
    if (result.success) {
      if (twoFARememberMe) localStorage.setItem('rememberedEmail', formData.email);
      toast.success(t('auth.loginSuccess')); navigate('/dashboard');
    }
    else { setTwoFAError(result.error || 'Gecersiz kod'); setOtpDigits(['','','','','','']); otpRefs[0]?.current?.focus(); }
  };

  const submitBackup = async () => {
    if (!backupCode.trim()) return;
    setTwoFALoading(true);
    setTwoFAError('');
    const result = await login2fa(twoFAUserId, backupCode.trim(), true, twoFARememberMe);
    setTwoFALoading(false);
    if (result.success) {
      if (twoFARememberMe) localStorage.setItem('rememberedEmail', formData.email);
      toast.success(t('auth.loginSuccess')); navigate('/dashboard');
    }
    else { setTwoFAError(result.error || 'Gecersiz yedek kod'); }
  };

  /* ── theme classes ── */
  const bg = isDark
    ? 'bg-[#0a0e1a]'
    : 'bg-gradient-to-br from-slate-50 via-cyan-50/30 to-slate-100';

  const cardBg = isDark
    ? 'bg-[#0f1629]/80 backdrop-blur-xl border-white/[0.06]'
    : 'bg-white/70 backdrop-blur-xl border-slate-200/80 shadow-xl shadow-cyan-900/5';

  const cardGlow = isDark
    ? 'from-cyan-500/30 via-transparent to-blue-500/20'
    : 'from-cyan-400/20 via-transparent to-blue-400/10';

  const textPrimary = isDark ? 'text-white' : 'text-slate-900';
  const textSecondary = isDark ? 'text-white/40' : 'text-slate-500';
  const textMuted = isDark ? 'text-white/60' : 'text-slate-600';
  const textFaint = isDark ? 'text-white/20' : 'text-slate-300';

  const inputCls = isDark
    ? 'bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:bg-white/[0.06] focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20'
    : 'bg-white/80 border-slate-200 text-slate-900 placeholder:text-slate-300 focus:bg-white focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20';

  const topBarBtn = isDark
    ? 'text-white/50 hover:text-white hover:bg-white/5'
    : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100';

  const dropdownCls = isDark
    ? 'bg-[#141b2d] border-white/10 text-white'
    : 'bg-white border-slate-200 text-slate-800';

  const dropdownItemActive = isDark ? 'bg-white/10' : 'bg-slate-100';
  const dropdownItemHover = isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50';

  const accentLink = isDark ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700';
  const forgotLink = isDark ? 'text-white/30 hover:text-cyan-400' : 'text-slate-400 hover:text-cyan-600';
  const eyeBtn = isDark ? 'text-white/25 hover:text-white/60' : 'text-slate-300 hover:text-slate-500';

  const dividerLine = isDark ? 'to-white/[0.06]' : 'to-slate-200/60';
  const dotColor = isDark ? 'bg-cyan-500/40' : 'bg-cyan-600/30';

  const logoGrad = 'from-cyan-400 to-blue-600';
  const logoText = isDark ? 'text-white/90' : 'text-slate-800';

  return (
    <div className={`min-h-screen relative overflow-hidden transition-colors duration-500 ${bg}`} data-testid="login2-page">
      {/* ── background ── */}
      <div className="absolute inset-0">
        {isDark && <div className="absolute inset-0 bg-gradient-to-br from-[#0a0e1a] via-[#0d1525] to-[#0a1628]" />}
        <NetworkCanvas isDark={isDark} />
        <HexGrid isDark={isDark} />
        <ScanLine isDark={isDark} />
        <DataStream side="left" isDark={isDark} />
        <DataStream side="right" isDark={isDark} />
        {/* radial glow */}
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-[120px] ${isDark ? 'bg-cyan-500/[0.03]' : 'bg-cyan-400/[0.06]'}`} />
        <div className={`absolute top-0 left-0 w-96 h-96 rounded-full blur-[100px] ${isDark ? 'bg-blue-600/[0.04]' : 'bg-blue-400/[0.05]'}`} />
        <div className={`absolute bottom-0 right-0 w-96 h-96 rounded-full blur-[100px] ${isDark ? 'bg-indigo-600/[0.04]' : 'bg-indigo-400/[0.04]'}`} />
      </div>

      {/* ── top bar ── */}
      <div className={`relative z-20 flex items-center justify-between px-6 py-4 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
        <div className="flex items-center gap-2.5">
          <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${logoGrad} flex items-center justify-center shadow-lg shadow-cyan-500/20`}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
            </svg>
          </div>
          <span className={`font-semibold text-lg tracking-tight ${logoText}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
            {settings.siteName || 'OMS Portal'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={toggleTheme} className={`${topBarBtn} h-8 w-8 p-0`} data-testid="login2-theme-toggle">
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={`${topBarBtn} gap-1.5 h-8 px-2`}>
                <Globe className="h-3.5 w-3.5" />
                <span className="text-xs">{LANGUAGES.find(l => l.code === language)?.flag}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={`${dropdownCls} min-w-[140px]`}>
              {LANGUAGES.map(lang => (
                <DropdownMenuItem key={lang.code} onClick={() => setLanguage(lang.code)} className={`${dropdownItemHover} cursor-pointer ${language === lang.code ? dropdownItemActive : ''}`}>
                  <span className="mr-2">{lang.flag}</span>{lang.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ── centered form ── */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-72px)] px-4">
        <div className={`w-full max-w-[420px] transition-all duration-700 delay-200 ${mounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}`}>

          {/* Firma domain header */}
          {!domainConfig.isMainDomain && domainConfig.firmId && (
            <div className="mb-6 flex items-center gap-3 px-1">
              {domainConfig.logoUrl ? (
                <img src={domainConfig.logoUrl} alt={domainConfig.firmName || 'Logo'} className={`h-10 w-auto rounded-lg p-1.5 ${isDark ? 'bg-white/10' : 'bg-slate-100'}`} />
              ) : (
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-slate-100'}`}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(14,116,144,0.8)" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                </div>
              )}
              <div>
                <h2 className={`font-semibold ${textPrimary}`}>{domainConfig.siteTitle || domainConfig.firmName || 'Portal'}</h2>
                {domainConfig.siteDescription && <p className={`text-xs ${textSecondary}`}>{domainConfig.siteDescription}</p>}
              </div>
            </div>
          )}

          {/* ── glass card ── */}
          <div className="relative group">
            <div className={`absolute -inset-[1px] rounded-2xl bg-gradient-to-br ${cardGlow} opacity-60 group-hover:opacity-100 transition-opacity duration-500`} />
            <div className={`relative rounded-2xl ${cardBg} p-8 overflow-hidden`}>

              {/* ── LOGIN FORM ── */}
              <div className={`transition-all duration-500 ${twoFAStep ? 'opacity-0 max-h-0 pointer-events-none -translate-x-8' : 'opacity-100 max-h-[600px] translate-x-0'}`}>
                <div className="mb-8">
                  <h1 className={`text-[26px] font-bold tracking-tight ${textPrimary}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {t('auth.loginTitle')}
                  </h1>
                  <p className={`text-sm mt-1.5 ${textSecondary}`}>
                    {canRegister(domainConfig) ? (
                      <>{t('auth.noAccount')}{' '}<Link to="/register" className={`${accentLink} font-medium transition-colors`}>{t('common.register')}</Link></>
                    ) : (
                      <span>{t('auth.welcomeBack')}</span>
                    )}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="email2" className={`text-xs font-medium uppercase tracking-wider ${textMuted}`}>{t('auth.email')}</Label>
                    <Input id="email2" type="email" placeholder="name@company.com" value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })} required
                      className={`h-11 transition-all ${inputCls}`} data-testid="login2-email-input" />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="password2" className={`text-xs font-medium uppercase tracking-wider ${textMuted}`}>{t('auth.password')}</Label>
                      <Link to="/forgot-password" className={`text-xs transition-colors ${forgotLink}`}>{t('auth.forgotPassword')}</Link>
                    </div>
                    <div className="relative">
                      <Input id="password2" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })} required
                        className={`h-11 pr-10 transition-all ${inputCls}`} data-testid="login2-password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${eyeBtn}`}>
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Remember Me */}
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="rememberMe" 
                      checked={rememberMe} 
                      onCheckedChange={(checked) => setRememberMe(checked)} 
                      className={isDark ? 'border-white/20 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500' : 'border-slate-300 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600'}
                      data-testid="remember-me-checkbox"
                    />
                    <Label htmlFor="rememberMe" className={`text-sm cursor-pointer select-none ${textMuted}`}>
                      {t('auth.rememberMe') || 'Beni hatırla'}
                    </Label>
                  </div>

                  <Button type="submit" disabled={isLoading}
                    className="w-full h-11 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/30 transition-all duration-300 border-0"
                    data-testid="login2-submit-btn">
                    {isLoading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('common.loading')}</>
                    ) : (
                      <span className="flex items-center gap-2">
                        {t('common.login')}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                      </span>
                    )}
                  </Button>

                  {/* Biometric Login */}
                  {biometricAvailable && rememberedEmail && (
                    <div className="relative">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-slate-200/60'}`} />
                        <span className={`text-xs ${textSecondary}`}>{t('auth.orBiometric') || 'veya'}</span>
                        <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-slate-200/60'}`} />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleBiometricLogin}
                        disabled={biometricLoading}
                        className={`w-full h-11 gap-2 transition-all duration-300 ${
                          isDark 
                            ? 'border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500/50' 
                            : 'border-cyan-200 text-cyan-700 hover:bg-cyan-50 hover:border-cyan-300'
                        }`}
                        data-testid="biometric-login-btn"
                      >
                        {biometricLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Fingerprint className="h-5 w-5" />
                        )}
                        {t('auth.biometricLogin') || 'Biyometrik Giriş'}
                      </Button>
                    </div>
                  )}
                </form>
              </div>

              {/* ── 2FA STEP ── */}
              <div className={`transition-all duration-500 ${twoFAStep ? 'opacity-100 max-h-[600px] translate-x-0' : 'opacity-0 max-h-0 pointer-events-none translate-x-8'}`}>
                {/* shield icon */}
                <div className="flex justify-center mb-5">
                  <div className="relative">
                    <div className={`h-16 w-16 rounded-2xl flex items-center justify-center ${isDark ? 'bg-cyan-500/10' : 'bg-cyan-50'} animate-pulse-slow`}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="text-cyan-500" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        <path d="M9 12l2 2 4-4"/>
                      </svg>
                    </div>
                    <div className="absolute -inset-2 rounded-3xl bg-cyan-500/10 blur-xl animate-pulse" />
                  </div>
                </div>

                <h2 className={`text-xl font-bold text-center mb-1 ${textPrimary}`}>{t('auth.twoFAVerification')}</h2>
                <p className={`text-sm text-center mb-6 ${textSecondary}`}>
                  {twoFAUserName && <span className="font-medium">{twoFAUserName}</span>}
                  {!useBackup ? `, ${t('auth.twoFALoginPrompt')}` : `, ${t('auth.twoFALoginBackupPrompt')}`}
                </p>

                {!useBackup ? (
                  <>
                    {/* OTP 6-digit input */}
                    <div className="flex justify-center gap-2.5 mb-4" data-testid="otp-input-group">
                      {otpDigits.map((digit, i) => (
                        <input
                          key={i}
                          ref={otpRefs[i]}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(i, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(i, e)}
                          onPaste={i === 0 ? handleOtpPaste : undefined}
                          className={`w-12 h-14 text-center text-2xl font-mono font-bold rounded-xl border-2 outline-none transition-all duration-200 ${
                            isDark
                              ? `bg-white/[0.04] border-white/10 text-white focus:border-cyan-400 focus:bg-white/[0.08] focus:shadow-[0_0_15px_rgba(56,189,248,0.15)]`
                              : `bg-white border-slate-200 text-slate-900 focus:border-cyan-500 focus:bg-cyan-50/30 focus:shadow-[0_0_15px_rgba(14,116,144,0.1)]`
                          } ${digit ? (isDark ? 'border-cyan-500/50' : 'border-cyan-400') : ''}`}
                          data-testid={`otp-digit-${i}`}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  /* Backup code input */
                  <div className="mb-4">
                    <Input
                      type="text"
                      placeholder="XXXXXXXX"
                      value={backupCode}
                      onChange={(e) => { setBackupCode(e.target.value); setTwoFAError(''); }}
                      onKeyDown={(e) => e.key === 'Enter' && submitBackup()}
                      className={`h-12 text-center text-lg font-mono tracking-widest ${inputCls}`}
                      data-testid="backup-code-input"
                    />
                  </div>
                )}

                {/* error */}
                {twoFAError && (
                  <p className="text-red-500 text-sm text-center mb-3 animate-shake" data-testid="2fa-error">{twoFAError}</p>
                )}

                {/* verify button (for backup or manual submit) */}
                {(useBackup || otpDigits.every(d => d)) && (
                  <Button
                    onClick={() => useBackup ? submitBackup() : submit2FA(otpDigits.join(''))}
                    disabled={twoFALoading}
                    className="w-full h-11 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold shadow-lg shadow-cyan-500/20 transition-all duration-300 border-0 mb-3"
                    data-testid="2fa-verify-btn"
                  >
                    {twoFALoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.confirm')}
                  </Button>
                )}

                {/* toggle backup */}
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => { setUseBackup(!useBackup); setTwoFAError(''); }}
                    className={`text-xs transition-colors ${forgotLink}`}
                    data-testid="toggle-backup-btn"
                  >
                    {useBackup ? t('auth.twoFAUseAuthCode') : t('auth.twoFAUseBackupCode')}
                  </button>
                </div>

                {/* back button */}
                <div className="flex justify-center mt-3">
                  <button
                    type="button"
                    onClick={() => { setTwoFAStep(false); setOtpDigits(['','','','','','']); setTwoFAError(''); setUseBackup(false); }}
                    className={`text-xs transition-colors ${forgotLink}`}
                    data-testid="2fa-back-btn"
                  >
                    {t('auth.twoFABackToLogin')}
                  </button>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <div className={`flex-1 h-px bg-gradient-to-r from-transparent ${dividerLine}`} />
                <div className="flex gap-1">
                  {[0,1,2].map(i => <div key={i} className={`w-1 h-1 rounded-full ${dotColor} animate-pulse`} style={{ animationDelay: `${i * 0.3}s` }} />)}
                </div>
                <div className={`flex-1 h-px bg-gradient-to-l from-transparent ${dividerLine}`} />
              </div>
            </div>
          </div>

          <p className={`text-center text-xs mt-6 transition-all duration-700 delay-500 ${textFaint} ${mounted ? 'opacity-100' : 'opacity-0'}`}>
            {t('auth.twoFASecureConnection')}
          </p>
        </div>
      </div>

      <style>{`
        @keyframes float-hex { 0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.4; } 50% { transform: translateY(-20px) rotate(30deg); opacity: 0.8; } }
        @keyframes scan-line { 0% { top: -2%; opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { top: 102%; opacity: 0; } }
        @keyframes data-fall { 0% { transform: translateY(-100%); opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { transform: translateY(100vh); opacity: 0; } }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 20% { transform: translateX(-6px); } 40% { transform: translateX(6px); } 60% { transform: translateX(-4px); } 80% { transform: translateX(4px); } }
        @keyframes pulse-slow { 0%, 100% { opacity: 0.8; } 50% { opacity: 1; } }
        .animate-float-hex { animation: float-hex ease-in-out infinite; }
        .animate-scan-line { animation: scan-line 6s linear infinite; }
        .animate-data-fall { animation: data-fall linear infinite; }
        .animate-shake { animation: shake 0.4s ease-out; }
        .animate-pulse-slow { animation: pulse-slow 2s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default Login2;
